import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { PencilSimpleIcon } from '@phosphor-icons/react/dist/csr/PencilSimple';
import { PlusIcon } from '@phosphor-icons/react/dist/csr/Plus';

import { UserActions } from './UserActions';
import { UserFormDialog } from './UserFormDialog';
import { PageActions } from '../../app/shell/PageActions';
import { Button } from '../../design-system/primitives/Button/Button';
import {
  ClientDataTable,
  type TableFilter,
} from '../../design-system/composites/DataTable/ClientDataTable';
import {
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import { ApiError } from '../../lib/api/client';
import { usersApi, rolesApi, type AdminUser, type Role } from '../../lib/api/users';
import { referenceName } from '../../lib/format/referenceName';
import styles from './UserListScreen.module.css';

/**
 * The administrator user list — and the permissions that decide what is on it.
 *
 * `permissions` and `currentUserUuid` are PROPS, not read from a session
 * context, so the component is testable without one and the whole gating
 * rule collapses to a single function, `can()`. Every row control checks two
 * things before it renders: does the caller hold the permission, and may they
 * use it on THIS row — the second question is `uuid !== currentUserUuid` for
 * the three actions the server-side policy refuses against yourself
 * (deactivate/activate, role assignment, resetting a second factor), and
 * `mfa_enabled || mfa_required` for the reset-MFA control specifically
 * (`UserActions.tsx`, where the gate actually lives). NOT `mfa_enabled`
 * alone: an account with `mfa_required` set and no enrolled factor is
 * locked out of enrolment (`202 enrolment_required` at login, and
 * enrolment sits behind `auth:sanctum` with no session to reach it), and
 * `DELETE /users/{user}/mfa` is the one control that rescues it — gating
 * on `mfa_enabled` alone hid that control from exactly the accounts that
 * needed it, a real-browser-verification defect, not a hypothetical. The
 * control is hidden only when NEITHER flag is set — genuinely nothing to
 * reset. "Edit" (task 13b) is the same
 * question with the opposite answer: `UserPolicy::update` has NO
 * self-refusal — checked against the server rather than assumed, the way
 * task 12's review caught `resetPassword`'s identical exception — so the
 * button's own visibility carries no `!isSelf`, and only the role picker
 * INSIDE the dialog it opens keeps one (`rowCanAssignRoles` below).
 *
 * HIDING A CONTROL THE API WOULD REFUSE IS THE WHOLE POINT. A supervisor who
 * can only view sees no buttons that would 403; an administrator viewing
 * their own row sees no button that would take down the session they are
 * using right now.
 *
 * Follows `HomePage.tsx` for the `DataTable` wiring (one `DataTable`, four
 * list states, no second copy of the state machine) and
 * `MfaEnrolmentScreen.tsx` for the failure/reference pattern: a fixed
 * region carries the support reference so a failed load can be traced, and
 * it is deliberately NOT inside `DataTable`'s own `role="alert"` box — that
 * box's copy and its built-in retry button are shared with five other
 * lists, and neither carries a request id slot.
 *
 * `status` DRIVES `DataTable` AND ONLY `DATATABLE` — the fetch lifecycle,
 * nothing else. It is written in exactly one place, `load()`, which clears
 * `reference` as its own first statements. A row action's own failure never
 * touches it: `activate`/`deactivate`/`resetPassword`/`resetMfa` all live in
 * `UserActions` now (task 13a), each with its own confirmation and its own
 * failure surface — a still-open `Dialog` for the three that confirm, and a
 * small row-scoped notice for Activate, which does not. Neither reaches back
 * into this screen's `status`. That was measured, not assumed: an earlier
 * version of this screen routed a row action's failure through
 * `setStatus('error')` and it replaced the whole table with "The list of
 * user accounts could not be loaded" on a single refused click, hiding rows
 * an administrator could still act on to report an error about the one row
 * that could not be changed.
 *
 * "ADD USER" AND "EDIT" (task 13b, the second folded from a separate "Edit
 * roles" after review) both open the same `UserFormDialog` — the row's own
 * `AdminUser` for the second, nothing for the first, which is exactly the
 * prop that switches it between edit and create. `onSaved` merges the
 * returned account into `rows` (an existing uuid replaces its row; a new one
 * is appended) rather than refetching, for the same reason `UserActions`'
 * `onChanged` does: a refetch flashes the loading skeleton over every row to
 * reflect a change to one.
 *
 * "Edit" is shown to a caller who holds EITHER `user.update` or
 * `user.role.assign` against this row — the dialog itself decides what that
 * caller can actually do inside it: `RoleCheckboxes` is gated on
 * `canAssignRoles`, computed per row (or, for the shared dialog instance, per
 * `editingUser`) with the self-refusal `user.role.assign` carries and
 * `user.update` does not. An `user.update`-only caller therefore sees "Edit"
 * on every row, including their own, and never sees a checkbox; a caller who
 * holds only `user.role.assign` sees it on every OTHER row and not their own.
 *
 * THE ROLE CATALOGUE is fetched here, once, gated on `user.role.assign` —
 * the same permission `GET /roles` itself needs server-side — and handed down
 * to the dialog as a prop. A caller without it never issues the request; the
 * dialog's own `roles.length === 0` already disables the picker for exactly
 * that case, so a failed fetch degrades to the same thing rather than to an
 * error surface of its own.
 */

export interface UserListScreenProps {
  permissions: readonly string[];
  currentUserUuid: string;
}

interface UserRow extends Row, AdminUser {
  /**
   * The dispensary's name in the reader's language, FLATTENED ONTO THE ROW.
   *
   * Not cosmetic plumbing: `ClientDataTable` offers sort on a column only
   * when some row carries a raw value under its key, and its search box walks
   * a row's PRIMITIVE values. `dispensary` is an object, so a column keyed on
   * it would be neither sortable nor searchable — the same trap the `roles`
   * column fell into, where typing a role name into the search box matched
   * nothing at all. A plain string gets both for free.
   *
   * '' for an unscoped account, which sorts those together at one end rather
   * than scattering them.
   */
  dispensary_name: string;
}

function toRow(user: AdminUser, language: string): UserRow {
  return {
    ...user,
    id: user.uuid,
    dispensary_name: user.dispensary === null ? '' : referenceName(user.dispensary, language),
  };
}

export function UserListScreen({ permissions, currentUserUuid }: UserListScreenProps) {
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly UserRow[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  const [roles, setRoles] = useState<readonly Role[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | undefined>(undefined);

  function can(permission: string): boolean {
    return permissions.includes(permission);
  }

  const reportLoadFailure = useCallback((error: unknown): void => {
    if (!(error instanceof ApiError)) throw error;
    setReference(error.requestId ?? null);
    setStatus('error');
  }, []);

  const load = useCallback(async () => {
    setStatus('loading');
    setReference(null);

    try {
      const list = await usersApi.list();
      const mapped = list.map((user) => toRow(user, i18n.language));
      setRows(mapped);
      setStatus(mapped.length === 0 ? 'empty' : 'ready');
    } catch (error) {
      reportLoadFailure(error);
    }
    // `i18n.language` because `toRow` localises the dispensary name into the
    // row. Switching language re-runs the load, which is a request this list
    // would otherwise not make — acceptable, and the alternative (localising
    // in the column's renderer) gives up sort and search on that column, for
    // the reason `UserRow.dispensary_name` explains.
  }, [reportLoadFailure, i18n.language]);

  /**
   * `UserActions.onChanged` — a row changed, merged in place rather than
   * refetched. See its own docblock for why: a refetch flashes the loading
   * skeleton over four other rows to reflect a change to one.
   */
  function applyRowChange(uuid: string, changes: Partial<AdminUser>): void {
    setRows((current) => current.map((row) => (row.id === uuid ? { ...row, ...changes } : row)));
  }

  useEffect(() => {
    void load();
  }, [load]);

  // The role catalogue `UserFormDialog`'s picker needs. Gated on the same
  // permission `GET /roles` itself needs server-side, so a caller who lacks
  // it never issues the request; an `ApiError` failure just leaves `roles`
  // empty, which disables the picker exactly as if the permission were
  // absent. A fault in our own code is a different thing and is NOT
  // swallowed alongside it — same rule `reportLoadFailure` above applies to
  // `load()`'s own failures.
  useEffect(() => {
    if (!permissions.includes('user.role.assign')) return;
    let cancelled = false;

    async function loadRoles(): Promise<void> {
      try {
        const list = await rolesApi.list();
        if (!cancelled) setRoles(list);
      } catch (error) {
        if (!(error instanceof ApiError)) throw error;
      }
    }

    void loadRoles();
    return () => {
      cancelled = true;
    };
  }, [permissions]);

  function openCreateForm(): void {
    setEditingUser(undefined);
    setFormOpen(true);
  }

  function openEditForm(user: AdminUser): void {
    setEditingUser(user);
    setFormOpen(true);
  }

  function closeForm(): void {
    setFormOpen(false);
  }

  /** `UserFormDialog.onSaved` — merged in place, appended if new. See the
   *  component docblock for why this is not a refetch. */
  function handleSaved(saved: AdminUser): void {
    setRows((current) => {
      const exists = current.some((row) => row.id === saved.uuid);
      return exists
        // `toRow` again, not `{ ...row, ...saved }`: the spread would keep
        // the OLD `dispensary_name`, so an edit that moved somebody to another
        // dispensary would show the previous one until the next load.
        ? current.map((row) => (row.id === saved.uuid ? toRow(saved, i18n.language) : row))
        : [...current, toRow(saved, i18n.language)];
    });
    // NOT `status !== 'ready'`. `Add user` renders on `can('user.create')`
    // regardless of `status` — including 'error' — so that shape flipped a
    // FAILED load to 'ready' the moment a create succeeded: `totalCount`
    // became 1, and both the load-failure alert and its retry control
    // vanished, telling the administrator this installation has exactly one
    // account. 'empty' is the only status a save legitimately clears: the
    // list was genuinely empty, and now it is not.
    if (status === 'empty') setStatus('ready');
    setFormOpen(false);
  }

  function roleLabel(role: Role): string {
    return i18n.language.startsWith('ar') ? role.label_ar : role.label_en;
  }

  function renderRoles(row: Row): ReactNode {
    const user = row as UserRow;
    const rolesText = user.roles.length === 0 ? null : user.roles.map(roleLabel).join(', ');
    // The role-mandated lockout `cd4d595` fixed only the account-flag half
    // of: `mfa_required` set, no enrolled factor, enrolment unreachable
    // because it sits behind the session the account cannot get.
    // `AdminUserResource` publishes the raw `mfa_required` COLUMN, so a
    // role-mandated account (no row-level flag, no factor) reads
    // `mfa_required: false` and gives no signal here otherwise. The data to
    // compute this was already on the wire (Task 13r put `mfa_required` on
    // `RoleResource`) and unused until now.
    const lockedAtEnrolment = user.roles.some((role) => role.mfa_required) && !user.mfa_enabled;

    return (
      <>
        {rolesText}
        {lockedAtEnrolment ? (
          <span className={styles['mfaLockout']} data-testid="userMfaLockout">
            {t('admin.mfaLockedIndicator')}
          </span>
        ) : null}
      </>
    );
  }

  function renderStatus(row: Row): ReactNode {
    return (row as UserRow).is_active ? t('admin.statusActive') : t('admin.statusInactive');
  }

  function renderActions(row: Row): ReactNode {
    const user = row as UserRow;
    const isSelf = user.uuid === currentUserUuid;
    // `user.role.assign` carries the same self-refusal as the other three
    // policy-refused actions this screen already hides — `user.update` does
    // NOT (`UserPolicy::update` checks the permission and `targetIsLive`
    // only), so a caller who holds only `user.update` still sees this
    // control on their own row.
    const rowCanAssignRoles = can('user.role.assign') && !isSelf;

    return (
      <div className={styles['actions']}>
        {can('user.update') || rowCanAssignRoles ? (
          <Button
            key="edit"
            variant="ghost"
            icon={<PencilSimpleIcon size={16} />}
            onClick={() => {
              openEditForm(user);
            }}
          >
            {t('admin.edit')}
          </Button>
        ) : null}

        <UserActions
          user={user}
          permissions={permissions}
          isSelf={isSelf}
          onChanged={applyRowChange}
        />
      </div>
    );
  }

  /**
   * Two facets, and neither is answerable by the search box above them.
   * `is_active` is a boolean the Status column renders as a word, and
   * `roles` is an ARRAY OF OBJECTS — `searchableText` walks a row's
   * primitive values and skips it entirely, so typing a role name into the
   * search box has always matched nothing at all.
   *
   * The role facet is empty for a caller without `user.role.assign`: that
   * permission is what `GET /roles` needs, so `roles` never loaded for them
   * and a select with only "All" in it is a control that cannot do
   * anything. `filters` drops it rather than rendering it inert.
   */
  /*
   * Built from the rows themselves rather than `GET /reference/dispensaries`:
   * a dispensary with no accounts is an option that can only ever return an
   * empty table, and this costs no second request. `Set` over the uuid, since
   * two rows naming the same dispensary must not produce two options.
   */
  const dispensaryOptions = (() => {
    const seen = new Map<string, string>();
    let anyUnscoped = false;

    for (const row of rows) {
      if (row.dispensary === null) {
        anyUnscoped = true;
        continue;
      }
      seen.set(row.dispensary.uuid, row.dispensary_name);
    }

    const options = [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, i18n.language));

    // Offered only when some account actually is unscoped — a filter that can
    // only return nothing is a control that cannot do anything, the same rule
    // the role facet below applies to itself.
    return anyUnscoped
      ? [{ value: '', label: t('admin.dispensaryNone') }, ...options]
      : options;
  })();

  const filters: readonly TableFilter[] = [
    {
      id: 'status',
      label: t('admin.columnStatus'),
      key: 'is_active',
      options: [
        { value: 'true', label: t('admin.statusActive') },
        { value: 'false', label: t('admin.statusInactive') },
      ],
    },
    ...(dispensaryOptions.length === 0
      ? []
      : [
          {
            id: 'dispensary',
            label: t('admin.columnDispensary'),
            // No `key`: '' must match a NULL dispensary, and the default
            // `String(row[key]) === value` comparison would also match a row
            // whose name happens to be empty for another reason.
            match: (row: Row, value: string) =>
              value === ''
                ? (row as UserRow).dispensary === null
                : (row as UserRow).dispensary?.uuid === value,
            options: dispensaryOptions,
          },
        ]),
    ...(roles.length === 0
      ? []
      : [
          {
            id: 'role',
            label: t('admin.columnRoles'),
            // No `key`: the value lives inside an array of objects, which the
            // default `String(row[key]) === value` comparison cannot reach.
            match: (row: Row, value: string) =>
              (row as UserRow).roles.some((role) => role.name === value),
            options: roles.map((role) => ({ value: role.name, label: roleLabel(role) })),
          },
        ]),
  ];

  const columns: readonly Column[] = [
    { key: 'full_name', label: t('admin.columnName') },
    { key: 'username', label: t('admin.columnUsername'), neverMirrors: true },
    { key: 'email', label: t('admin.columnEmail'), neverMirrors: true },
    { key: 'roles', label: t('admin.columnRoles'), render: renderRoles },
    /*
     * NO `render`. The row carries `dispensary_name` as a plain string, so
     * the default cell shows it, `ClientDataTable` offers sort on it, and the
     * search box matches it — all three for free. A custom renderer reading
     * `row.dispensary` would give up the last two.
     */
    { key: 'dispensary_name', label: t('admin.columnDispensary') },
    { key: 'status', label: t('admin.columnStatus'), render: renderStatus },
    { key: 'actions', label: t('admin.columnActions'), render: renderActions },
  ];

  return (
    <div className={styles['page']}>
      {can('user.create') ? (
        <PageActions>
          <Button onClick={openCreateForm} icon={<PlusIcon size={16} />}>
            {t('admin.addUser')}
          </Button>
        </PageActions>
      ) : null}

      <ClientDataTable
        caption={t('admin.title')}
        columns={columns}
        rows={rows}
        filters={filters}
        status={status}
        empty={{ body: t('admin.empty') }}
        error={{ title: t('admin.loadFailed') }}
      />

      {/*
        Deliberately OUTSIDE DataTable's own alert box. That box's copy and
        retry button are shared with five other lists and carry no request-id
        slot; this is a fixed, always-mounted region — see
        MfaEnrolmentScreen.tsx's alert/reference pattern — so a failed load
        stays traceable and a retry does not depend on DataTable knowing
        anything about this screen.
      */}
      {status === 'error' ? (
        <div className={styles['recovery']}>
          <Button
            variant="secondary"
            icon={<ArrowClockwiseIcon size={16} />}
            onClick={() => {
              void load();
            }}
          >
            {t('admin.retry')}
          </Button>

          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="userListRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}

      <UserFormDialog
        open={formOpen}
        user={editingUser}
        roles={roles}
        // Create has no row to refuse against (`editingUser` is `undefined`,
        // and `undefined !== currentUserUuid` is always true); editing keeps
        // the same self-refusal `renderActions`' `rowCanAssignRoles` computes
        // per row, because this one dialog instance serves whichever row was
        // opened.
        canAssignRoles={can('user.role.assign') && editingUser?.uuid !== currentUserUuid}
        onClose={closeForm}
        onSaved={handleSaved}
      />
    </div>
  );
}
